import React, { useState } from "react";
import { Button, Card, List, Typography, Space, message } from "antd";
import useAPI from "hooks/API";
import DateSelectionForm from "components/DateSelectionForm";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
interface Update {
  state: "idle" | "inprogress" | "success" | "failure";
  message?: string;
}

export const SourceParityRateUpdates = () => {
  const [tefasUpdate, setTefasUpdate] = useState<Update>({ state: "idle" });
  const [externalUpdate, setExternalUpdate] = useState<Update>({
    state: "idle",
  });
  const api = useAPI();

  const handleUpdate = (
    resource: string,
    statusSetter: (u: Update) => void,
  ) => {
    statusSetter({ state: "inprogress" });
    api
      .post(`/admin/resources/update/${resource}`)
      .then((res) => {
        const numRes = parseInt(res);
        if (numRes > 0)
          statusSetter({ state: "success", message: `${res} values updated` });
        else statusSetter({ state: "success", message: `No updates` });
      })
      .catch(() => {
        statusSetter({ state: "failure", message: "Failed to update" });
      });
  };

  const UpdateCard = ({
    title,
    update,
    handler,
  }: {
    title: string;
    update: Update;
    handler: () => void;
  }) => {
    return (
      <Card bordered title={title} style={{ width: "40vw" }}>
        {update.message ? (
          <div style={{ marginBottom: "1em" }}>{update.message}</div>
        ) : null}
        <Button onClick={handler} loading={update.state === "inprogress"}>
          Update
        </Button>
      </Card>
    );
  };

  return (
    <div>
      <h2>Parity Updates</h2>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <UpdateCard
          title={"TEFAS"}
          update={tefasUpdate}
          handler={() => handleUpdate("tefas", setTefasUpdate)}
        />
        <UpdateCard
          title={"BP"}
          update={externalUpdate}
          handler={() => handleUpdate("external", setExternalUpdate)}
        />
        <BulkDateProcessor />
      </div>
    </div>
  );
};

type Status = "pending" | "success" | "failed";

interface DateResult {
  date: string;
  status: Status;
  retrying?: boolean;
}

const BulkDateProcessor: React.FC = () => {
  const [results, setResults] = useState<DateResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const api = useAPI();

  const refreshCache = async () => {
    try {
      setRefreshingCache(true);

      const response = await api.post(
        `/admin/resources/update/tefas/refreshCache`,
      );

      if (response != "ok") {
        throw new Error("Failed to refresh cache");
      }

      message.success("Cache refreshed successfully");
    } catch (e) {
      message.error("Cache refresh failed");
    } finally {
      setRefreshingCache(false);
    }
  };

  const retryDate = async (date: string) => {
    setResults((prev) =>
      prev.map((item) =>
        item.date === date
          ? { ...item, status: "pending", retrying: true }
          : item,
      ),
    );

    try {
      const response = await processDate(date);

      setResults((prev) =>
        prev.map((item) =>
          item.date === date
            ? {
                ...item,
                status: response == "ok" ? "success" : "failed",
                retrying: false,
              }
            : item,
        ),
      );
    } catch {
      setResults((prev) =>
        prev.map((item) =>
          item.date === date
            ? {
                ...item,
                status: "failed",
                retrying: false,
              }
            : item,
        ),
      );
    }
  };

  const processDate = async (date: string): Promise<string> => {
    // Replace with your actual API call
    const response = await api.post(`/admin/resources/update/tefas/${date}`);

    if (response != "ok") {
      throw new Error(`Failed for ${date}`);
    }
    return response;
  };

  const handleSubmit = async (dates: string[]) => {
    setLoading(true);

    const initialResults = dates.map((date) => ({
      date,
      status: "pending" as Status,
    }));

    setResults(initialResults);

    for (const date of dates) {
      try {
        await processDate(date);

        setResults((prev) =>
          prev.map((item) =>
            item.date === date ? { ...item, status: "success" } : item,
          ),
        );
      } catch (e) {
        setResults((prev) =>
          prev.map((item) =>
            item.date === date ? { ...item, status: "failed" } : item,
          ),
        );
      }
    }
    if(results.every(r=>r.status =="success")){

    }

    setLoading(false);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Card title="TEFAS Parity History">
        <DateSelectionForm
          onSubmit={handleSubmit}
          onlyPast
        />
      </Card>

      <Card
        title={
          <Space>
            <Space>
              <span>Processing Dates</span>
              <Typography.Text type="secondary">
                ({results.filter((item) => item.status !== "pending").length}/
                {results.length})
              </Typography.Text>
            </Space>
            <Button
              size="small"
              type="primary"
              onClick={refreshCache}
              loading={refreshingCache}
              disabled={results.length == 0 || results.some((r)=> r.status == "pending")}
            >
              Refresh Cache
            </Button>
          </Space>
        }
      >
        <div
          style={{
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          <List
            dataSource={results}
            renderItem={(item) => (
              <List.Item>
                <Space>
                  {item.status === "success" && (
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  )}

                  {item.status === "failed" && (
                    <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                  )}

                  {item.status === "pending" && (
                    <LoadingOutlined
                      style={{
                        color: "#faad14",
                        fontSize: 18,
                      }}
                    />
                  )}

                  <Typography.Text>{item.date}</Typography.Text>
                  {item.status === "failed" && (
                    <Button
                      key="retry"
                      size="small"
                      danger
                      onClick={() => retryDate(item.date)}
                      loading={item.retrying}
                    >
                      Retry
                    </Button>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </div>
      </Card>
    </Space>
  );
};

export default SourceParityRateUpdates;
